import { supabase } from '../supabase';
import type { AlmaQuestionBranch, AlmaQuestionOption } from '../../types';
import { ALMA_OPTION_LISTS } from './optionLists';
import type { FlowBranch, FlowCity, FlowListKey, FlowLists, FlowOption, FlowWalkingOption } from './types';

/** host_specialties has its own inactive "Other" row — never offer it; the flow's built-in Other covers it. */
const RESERVED_SPECIALTY_SLUGS = ['other'];

const WALKING_KEY = ALMA_OPTION_LISTS.find((l) => l.perGroupLabels)?.key ?? 'walking_comfort';

/**
 * Loads every Phase 1 list in one round of parallel queries. Each list fails
 * on its own: a failure is reported in `failed` (the step shows a retry),
 * never treated as an empty list (which would skip the step). Only active
 * rows are readable publicly, so no `active` filter is needed — it's added
 * anyway so an admin testing the flow sees what travelers see.
 */
export async function loadFlowLists(): Promise<FlowLists> {
  const [cities, bookable, specialties, branches, options] = await Promise.all([
    supabase.from('destinations').select('id, name, slug').eq('active', true).order('sort_order'),
    supabase.rpc('bookable_destination_ids'),
    supabase.from('host_specialties').select('id, name, slug').eq('active', true).order('sort_order'),
    supabase.from('alma_question_branches').select('*').eq('active', true).order('created_at'),
    supabase.from('alma_question_options').select('*').eq('active', true).order('sort_order'),
  ]);

  const failed: FlowListKey[] = [];
  if (cities.error) failed.push('cities');
  if (specialties.error) failed.push('specialties');
  if (branches.error || options.error) failed.push('branches');
  if (options.error) failed.push('walking');

  const optionRows = (options.data ?? []) as AlmaQuestionOption[];
  const toOption = (o: AlmaQuestionOption): FlowOption => ({ id: o.id, slug: o.slug, label: o.label });

  return {
    // A city can be picked only when it has enough hosts (see bookable_destination_ids).
    // If that list can't be read, every city stays open: a blip must not close the booking.
    cities: ((cities.data ?? []) as FlowCity[]).map((c) => {
      const open = bookable.error || !Array.isArray(bookable.data) ? null : (bookable.data as string[]);
      return open && !open.includes(c.id) ? { ...c, bookable: false } : c;
    }),
    specialties: ((specialties.data ?? []) as { id: string; name: string; slug: string }[])
      .filter((s) => !RESERVED_SPECIALTY_SLUGS.includes(s.slug))
      .map((s) => ({ id: s.id, slug: s.slug, label: s.name })),
    branches: ((branches.data ?? []) as AlmaQuestionBranch[]).map((b): FlowBranch => ({
      question_key: b.question_key,
      trigger_specialty_slug: b.trigger_specialty_slug,
      title: b.title,
      helper: b.helper,
      alma_line: b.alma_line,
      options: optionRows.filter((o) => o.question_key === b.question_key).map(toOption),
    })),
    walking: optionRows
      .filter((o) => o.question_key === WALKING_KEY)
      .map((o): FlowWalkingOption => ({ ...toOption(o), label_solo: o.label_solo, label_partner: o.label_partner, label_group: o.label_group })),
    failed,
  };
}
